import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { GraduationCap, X, ExternalLink } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useHelpVisibility } from "../hooks/useHelpVisibility";

/**
 * Bannière d'onboarding affichée UNE SEULE FOIS après la 1ère connexion
 * d'un établissement. Propose la lecture du Guide Établissement.
 *
 * Stratégie additive : ne modifie aucun composant existant. Le composant
 * s'auto-masque s'il n'est pas en contexte commerçant ou si déjà vu.
 *
 * Clés localStorage :
 *  - bypassgo_onboarding_guide_seen   → "1" si le marchand a cliqué « Plus tard » ou « Ne plus afficher »
 *  - bypassgo_onboarding_guide_dismissed_at → timestamp (statistique optionnelle)
 */
const LS_KEY_SEEN = "bypassgo_onboarding_guide_seen";
const LS_KEY_DISMISS_AT = "bypassgo_onboarding_guide_dismissed_at";

export default function OnboardingGuideBanner() {
  const loc = useLocation();
  const { role, commerce } = useAuth();
  const helpVis = useHelpVisibility();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Conditions strictes d'affichage
    if (role !== "commerce") return;
    if (!commerce?.emailVerified) return; // pas pendant la phase de vérif email
    if (commerce?.isBlocked) return;
    // Ne pas afficher sur les pages d'auth, sur /tv, sur les pages d'aide, etc.
    const path = loc.pathname || "";
    const blocked = ["/help", "/admin", "/display/", "/payment/", "/q/", "/tv", "/commerce/login", "/commerce/register"];
    if (blocked.some((p) => path.startsWith(p))) return;
    // Marquer comme vu si déjà cliqué
    if (localStorage.getItem(LS_KEY_SEEN) === "1") return;
    if (helpVis.visible === false) return; // respect du toggle admin
    setVisible(true);
  }, [loc.pathname, role, commerce, helpVis.visible]);

  const dismiss = () => {
    try {
      localStorage.setItem(LS_KEY_SEEN, "1");
      localStorage.setItem(LS_KEY_DISMISS_AT, String(Date.now()));
    } catch (_) {}
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-20 right-5 z-30 max-w-sm w-[calc(100%-2.5rem)] sm:w-96"
      data-testid="onboarding-guide-banner"
    >
      <div className="relative bg-white rounded-2xl shadow-2xl border border-[#002FA7]/20 overflow-hidden">
        {/* Bandeau coloré */}
        <div className="bg-gradient-to-br from-[#002FA7] to-[#003FC7] text-white px-5 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
            <GraduationCap className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-sm leading-tight">5 minutes pour maîtriser BypassGo</p>
            <p className="text-xs text-white/80 mt-0.5">Bienvenue parmi les partenaires</p>
          </div>
          <button
            onClick={dismiss}
            className="w-7 h-7 rounded-full hover:bg-white/15 flex items-center justify-center"
            aria-label="Fermer"
            data-testid="onboarding-guide-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Corps */}
        <div className="px-5 py-4">
          <p className="text-sm text-slate-700 leading-relaxed">
            Découvrez en un guide illustré toutes les fonctionnalités pour piloter
            efficacement votre accueil : gestion des files, écran TV, statistiques,
            personnalisation et bien plus.
          </p>
          <div className="mt-4 flex gap-2">
            <Link
              to="/help/establishment"
              onClick={dismiss}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-[#002FA7] text-white px-4 py-2.5 rounded-lg font-semibold text-sm hover:bg-[#001a87] transition"
              data-testid="onboarding-guide-read-btn"
            >
              <ExternalLink className="w-4 h-4" />
              Lire le guide
            </Link>
            <button
              onClick={dismiss}
              className="px-4 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-50 transition"
              data-testid="onboarding-guide-later-btn"
            >
              Plus tard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
